/**
 * useOnlineStatus hook
 *
 * Tracks browser online/offline state and provides:
 * - isOnline: current connectivity status
 * - wasOffline: true if the user was offline at any point this session
 * - offlineSince: timestamp when connectivity was lost (null if online)
 */
import { useState, useEffect, useCallback, useRef } from "react";

export interface UseOnlineStatusReturn {
  isOnline: boolean;
  wasOffline: boolean;
  offlineSince: number | null;
}

export function useOnlineStatus(): UseOnlineStatusReturn {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );
  const [wasOffline, setWasOffline] = useState(false);
  const [offlineSince, setOfflineSince] = useState<number | null>(null);
  const wasOfflineRef = useRef(false);

  const handleOnline = useCallback(() => {
    setIsOnline(true);
    setOfflineSince(null);
  }, []);

  const handleOffline = useCallback(() => {
    setIsOnline(false);
    setOfflineSince(Date.now());
    setWasOffline(true);
    wasOfflineRef.current = true;
  }, []);

  useEffect(() => {
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check initial state
    if (!navigator.onLine) {
      handleOffline();
    }

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [handleOnline, handleOffline]);

  return { isOnline, wasOffline, offlineSince };
}
